import React from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {getFiltersAlphabetically} from '../../../forecast-app/shared/components/filters/filter_util';
import {DATE_PICKER_STYLE, ELEMENT_TYPE, FILTER_SECTION, FILTER_TYPE} from '../../../constants';
import {FILTER_SECTIONS} from '../../../forecast-app/shared/components/filters/FilterWrapper';
import {buildThirdLevelNavHeader} from '../../../forecast-app/shared/components/headers/header-bar/header_bar';
import {UTILIZATION_DROPDOWN, UTILIZATION_PAGES, GROUP_BY} from './UtilizationReport';
import * as tracking from '../../../tracking';
import {getPeriodEyeOptions} from './Period';
import Util from '../../../forecast-app/shared/util/util';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';

export const HeaderBar = React.memo(
	({
		viewer,
		activePage,
		showResourceUtilization,
		handleSetFirstDropdownValue,
		groupBy,
		handleSetSecondDropdownValue,
		onChangeDates,
		selectedDates,
		selectedYear,
		onChangeYear,
		handleCsvDownload,
		getBottomReportHeader,
	}) => {
		const intl = useIntl();
		const getHeaderLeftContent = () => {};

		const getHeaderRightContent = () => {
			const peopleFilters = [
				FILTER_TYPE.PROJECT,
				FILTER_TYPE.PERSON,
				FILTER_TYPE.INTERNAL_TIME,
				FILTER_TYPE.TIME_OFF,
				FILTER_TYPE.ROLE,
				FILTER_TYPE.TEAM,
				FILTER_TYPE.PROJECT_STAGE,
				FILTER_TYPE.LABEL,
			];

			if (Util.hasSkills() && hasFeatureFlag('skills')) {
				peopleFilters.push(FILTER_TYPE.SKILL);
			}

			if (Util.hasDepartments(viewer.company.modules)) {
				peopleFilters.push(FILTER_TYPE.DEPARTMENT);
			}
			const projectFilters = [];
			const taskFilters = [];
			const filterProps = {
				defaultSection: FILTER_SECTIONS.PEOPLE,
				projectFilters,
				peopleFilters: getFiltersAlphabetically(peopleFilters, intl.formatMessage),
				taskFilters,
				appliedFiltersName: `filters-utilization-report`,
				filterSection: FILTER_SECTION.UTILIZATION_REPORT,
				viewer,
			};

			const additionalProps = {filterProps: filterProps};

			if (activePage === UTILIZATION_PAGES.PERIOD) {
				additionalProps.eyeProps = {
					eyeOptions: getPeriodEyeOptions(
						viewer.company.isUsingProjectAllocation,
						viewer.company.isUsingSchedulingPlanMode
					),
				};
			}

			const content = [];

			const filteredHeaderElements = getBottomReportHeader(additionalProps);

			content.push(...filteredHeaderElements);

			const groupingOptions = [
				{value: GROUP_BY.NONE, label: intl.formatMessage({id: 'common.no_grouping'})},
				{value: GROUP_BY.ROLE, label: intl.formatMessage({id: 'common.group_by_role'})},
			];
			if (Util.hasDepartments(viewer.company.modules)) {
				groupingOptions.push({
					value: GROUP_BY.DEPARTMENT,
					label: intl.formatMessage({id: 'common.group_by_department'}),
				});
			}
			content.push({
				type: ELEMENT_TYPE.DROPDOWN,
				dropdownOptions: groupingOptions,
				value: groupBy,
				callback: selectedGroupingOption => handleSetSecondDropdownValue(selectedGroupingOption.value),
				cy: 'canvas-search',
			});

			content.push({
				type: ELEMENT_TYPE.CSV,
				callback: handleCsvDownload,
			});

			const utilizationNumberOptions = [
				{
					value: false,
					label: intl.formatMessage({id: 'common.billable_utilization'}),
				},
				{
					value: true,
					label: intl.formatMessage({id: 'common.resource_utilization'}),
				},
			];

			const getContextDropdownValueToUpdate = showResourceUtilization => {
				return showResourceUtilization
					? UTILIZATION_DROPDOWN.RESOURCE_UTILIZATION
					: UTILIZATION_DROPDOWN.BILLABLE_UTILIZATION;
			};

			content.push({
				type: ELEMENT_TYPE.LEFT_DROPDOWN,
				dropdownOptions: utilizationNumberOptions,
				value: showResourceUtilization,
				callback: selectedUtilizationNumberOption => {
					tracking.trackElementClicked('Utilization type changed', {
						utilization_type: selectedUtilizationNumberOption.value
							? intl.formatMessage({id: 'common.resource_utilization'})
							: intl.formatMessage({id: 'common.billable_utilization'}),
					});
					handleSetFirstDropdownValue(getContextDropdownValueToUpdate(selectedUtilizationNumberOption.value));
				},
				placeholder: '',
				userpilot: 'utilization-number-dropdown',
			});

			if (activePage === UTILIZATION_PAGES.PERIOD) {
				content.push({
					type: ELEMENT_TYPE.DATE_PICKER,
					isNewDateRange: true,
					startDate: selectedDates.startDate,
					endDate: selectedDates.endDate,
					handleDateRangeChange: onChangeDates,
					colorInherited: true,
					compactShowYear: true,
					datePickerStyle: DATE_PICKER_STYLE.SLIM,
					calendarOffsetX: 0,
					clearable: false,
					userpilot: 'utilization-date-picker',
				});
			} else {
				content.push({
					type: ELEMENT_TYPE.YEAR_STEPPER,
					startYear: selectedYear,
					onYearChanged: onChangeYear,
				});
			}

			return content;
		};

		return buildThirdLevelNavHeader(getHeaderLeftContent(), getHeaderRightContent(), null, {noPadding: true}, false);
	}
);

HeaderBar.propTypes = {
	activePage: PropTypes.string.isRequired,
};
